// API Types - synced with packages/core-schemas/src/ponpon_core_schemas/models.py

type JobResultValue = string | number | boolean | null | object

// Enums
export type ProjectStatus = 'draft' | 'in_progress' | 'completed'
// Kept in sync with ``VALID_PRODUCTION_TARGETS`` in core/state_models.py.
// 'music_video' was present on the Python side and missing here, so the
// front end could not offer it even though the API accepts it and
// ``mv_enabled_for()`` turns the MV tools on for such projects.
export type ProductionTarget = 'webtoon' | 'video' | 'ads' | 'music_video'
export type AspectRatio = '16:9' | '9:16'
export type Genre = 'romance' | 'action' | 'fantasy' | 'thriller' | 'slice_of_life' | 'horror' | 'comedy' | 'drama' | 'mystery' | 'scifi'
export type Tone = 'comedic' | 'dramatic' | 'dark' | 'lighthearted' | 'romantic' | 'suspenseful'
export type CharacterRole = 'protagonist' | 'antagonist' | 'supporting' | 'minor'
export type LocationType = 'interior' | 'exterior'
// Note: 'age' removed - major age differences use separate Characters
export type VariantType = 'outfit' | 'hairstyle' | 'transformation' | 'physical_state'
export type TimeOfDay = 'morning' | 'day' | 'evening' | 'night'
export type ShotType = 'wide' | 'medium' | 'close_up' | 'extreme_close_up'
export type CameraAngle = 'eye_level' | 'high' | 'low' | 'dutch'
export type DialogueType = 'speech' | 'thought' | 'narration' | 'sfx' | 'whisper' | 'shout'
export type ChapterStatus = 'outlined' | 'generating' | 'completed'

// Global Models

// Style assets for a specific style variant
export interface StyleAssets {
  bible_sheet?: string
  portrait?: string
}

// Actor - reusable visual identity (like a talent agency roster)
export interface Actor {
  id: string
  name: string
  age?: number  // Optional age of the actor
  reference_images: string[]
  bible_sheet?: string
  portrait?: string
  style_variants: Record<string, StyleAssets>  // e.g., { pixar: { bible_sheet: "...", portrait: "..." } }
  visual_tags: string[]
  is_celebrity?: boolean  // True if actor is a celebrity/public figure (restricts video generation)
  created_at?: string
  updated_at?: string
}

// Role - narrative character (personality, arc)
export interface Role {
  id: string
  name: string
  role_type: CharacterRole
  personality: string
  background: string
  motivation: string
  arc: string
  character_ids: string[]  // Cast members for this role
}

// Story
export interface StoryBeat {
  beat: string
  description: string
}

export interface Story {
  id: string
  title: string
  logline: string
  genre: Genre
  tone: Tone
  themes: string[]
  target_audience: string
  episode_count: number
  synopsis: string
  story_beats: StoryBeat[]
}

// Character
export interface CharacterDescription {
  physical: string
  personality: string
  background: string
  motivation: string
}

export interface CharacterVariant {
  id: string
  type: VariantType
  name: string
  description: string
  visual_tags: string[]
  contexts: string[]
  sheet_path?: string
}

// Deprecated: Use CharacterVariant instead
export type CharacterOutfit = CharacterVariant

export interface CharacterAssets {
  reference_input?: string
  portrait?: string
  sheet?: string
  bible_sheet?: string  // Primary reference sheet (3-view turnaround)
  variants?: CharacterVariant[]
  // Deprecated: use 'variants' instead
  outfits?: CharacterOutfit[]
}

export interface Character {
  id: string
  name: string
  role_id?: string  // Links to Role
  actor_id?: string  // Links to global Actor (optional)
  age: string
  description: CharacterDescription
  visual_tags: string[]
  assets: CharacterAssets
  voice_description: string
  // Deprecated - kept for backward compatibility, use role_id to get Role.role_type
  role: CharacterRole
}

// Location
export interface LocationAssets {
  reference?: string
  reference_sheet?: string
}

export interface Location {
  id: string
  name: string
  type: LocationType
  description: string
  visual_tags: string[]
  assets: LocationAssets
}

// Panel
export interface Dialogue {
  character_id?: string
  text: string
  type: DialogueType
}

export interface PanelCharacter {
  character_id: string
  expression: string
  pose: string
  position: string
}

export interface PanelComposition {
  shot_type: ShotType
  angle: CameraAngle
  focus: string
}

export interface Panel {
  id: string
  number: number
  type: string
  composition: PanelComposition
  characters: PanelCharacter[]
  action: string
  dialogue: Dialogue[]
  sfx: string[]
  continues_from_previous: boolean
  continuity_note: string
  image_path?: string
  video_path?: string
}

// Scene
export interface Scene {
  id: string
  number: number
  location_id?: string
  time_of_day: TimeOfDay
  weather: string
  character_ids: string[]
  character_variants?: Record<string, string>  // {char_id: variant_id}
  description: string
  mood: string
  panels: Panel[]
  continues_from_previous_chapter: boolean
}

// Chapter
export interface Chapter {
  id: string
  number: number
  title: string
  summary: string
  status: ChapterStatus
  character_variants?: Record<string, string>  // {char_id: variant_id} - default for chapter
  scenes: Scene[]
}

// Project
// Cover assets
export interface CoverAssets {
  image_path?: string
  thumbnail_path?: string
  generated_at?: string
}

export interface Project {
  id: string
  name: string
  created_at: string
  updated_at: string
  status: ProjectStatus
  style: string  // Visual style (semi-realistic, 3d-animated, anime-watercolor)
  story?: Story
  roles: Role[]  // Narrative characters
  characters: Character[]  // Visual entities (cast)
  locations: Location[]
  chapters: Chapter[]
  cover?: CoverAssets  // Project cover image
  original_prompt: string
  reference_images: string[]
  has_celebrity?: boolean  // True if any character uses a celebrity actor (restricts video generation)
  is_demo?: boolean  // True if this is a demo project (read-only, publicly accessible)
}

// Cover
export interface CoverResponse {
  image_path?: string
  thumbnail_path?: string
  generated_at?: string
  has_cover: boolean
}

export interface ProjectNodePreview {
  name: string
  image_path: string
}

/**
 * Flattened, kind-tagged preview entry for dashboard cards.
 *
 * Priority order (consumer reads previews[0] as the cover):
 *   video > storyboard > image > character > location
 *
 * The frontend picks a render strategy from ``kind``:
 *   * ``video`` → ``<video muted autoplay loop playsInline>``
 *   * everything else → ``<img>``
 *
 * Source-of-truth: backend ``ProjectAssetPreview`` in
 * ``packages/core-schemas/src/ponpon_core_schemas/api_contracts.py``.
 */
export interface ProjectAssetPreview {
  kind: 'video' | 'storyboard' | 'image' | 'character' | 'location'
  name: string
  path: string
  node_type: string
}

// API Response types
export interface ProjectListItem {
  id: string
  name: string
  status: ProjectStatus
  story_expanded: boolean
  character_count: number
  location_count: number
  chapter_count: number
  created_at: string
  updated_at: string
  has_celebrity?: boolean
  is_demo?: boolean
  character_previews?: ProjectNodePreview[]
  location_previews?: ProjectNodePreview[]
  /**
   * Unified, kind-tagged preview list. Sorted by priority on the
   * backend. Consumers read previews[0] as the cover. Empty when the
   * project has produced no visual assets yet — card falls back to
   * the letter watermark.
   */
  previews?: ProjectAssetPreview[]
  /**
   * One-line story pitch shown on the dashboard card as the project's
   * description. Empty string until the story has been expanded.
   */
  logline?: string
}

export interface PaginationMeta {
  total: number
  limit: number
  offset: number
  has_more: boolean
}

export interface PaginatedResponse<T> {
  data: T[]
  pagination: PaginationMeta
}

export type JobStatus = 'pending' | 'running' | 'completed' | 'failed' | 'cancelled'

export interface JobMetadata {
  project_id?: string
  chapter_number?: number
  scene_number?: number
  panel_number?: number
  character_id?: string
  character_name?: string
  location_id?: string
  actor_id?: string
  actor_name?: string
  style?: string
  overwrite?: boolean
  has_feedback?: boolean
  generate_sheets?: boolean
}

export interface Job {
  id: string
  type: string
  status: JobStatus
  progress?: number
  total?: number
  result?: JobResultValue
  error?: string
  created_at: string
  started_at?: string
  completed_at?: string
  metadata?: JobMetadata
  // Phase 2 fields (ADR-0014)
  current_step?: string
  avg_step_duration_ms?: number
  last_updated?: string
}

export interface ApiError {
  error: {
    code: string
    message: string
    field?: string
  }
}

// Request types
export interface CreateProjectRequest {
  prompt: string
  name?: string
  production_target?: ProductionTarget
  style?: string
  aspect_ratio?: AspectRatio
}

/**
 * Project-level workflow config exposed under the canvas gear menu.
 *
 * Mirrors `WorkflowState.{style_id, aspect_ratio, production_target}` —
 * the project-level defaults applied to newly-created nodes. Existing
 * nodes keep their own `in_*` values stored in YDoc and are not
 * touched when these defaults change.
 */
/**
 * Mirrors the backend `AgentAutonomy = Literal["ask", "act"]`.
 *
 * `ask` — the Director stages work and waits for the user to confirm each
 * cost card. `act` — it runs the plan through to the end on its own, which
 * is what a multi-episode build needs; a user cannot sit and confirm a card
 * per clip across an episode.
 */
export type AgentAutonomy = 'ask' | 'act'

export const AGENT_AUTONOMY_VALUES: readonly AgentAutonomy[] = ['ask', 'act']

export interface ProjectConfig {
  style_id: string
  aspect_ratio: AspectRatio
  production_target: ProductionTarget
  agent_autonomy: AgentAutonomy
}

export interface UpdateProjectConfigRequest {
  style_id?: string
  aspect_ratio?: AspectRatio
  production_target?: ProductionTarget
  /**
   * Omitting `session_id` writes the PROJECT-level default, which is what the
   * canvas gear does. Session autonomy falls back to it, so the write takes
   * effect for any session that has not explicitly set its own mode.
   */
  agent_autonomy?: AgentAutonomy
  session_id?: string
}

export interface CreateProjectResponse {
  id: string
  name: string
  status: ProjectStatus
  created_at: string
  updated_at: string
  story_expanded: boolean
  character_count: number
  location_count: number
  chapter_count: number
}

export interface CharacterSpec {
  name: string
  file?: File
}

export interface ExpandStoryRequest {
  prompt: string
  genre?: Genre
  tone?: Tone
  episodes?: number
  characters?: CharacterSpec[]
}

export interface CreateCharacterRequest {
  name: string
  role?: CharacterRole
  age?: string
  description?: Partial<CharacterDescription>
  visual_tags?: string[]
}

export interface CreateLocationRequest {
  name: string
  type?: LocationType
  description?: string
  visual_tags?: string[]
}

export interface JobCreateResponse {
  job_id: string
  type: string
  status: JobStatus
  created_at: string
  metadata?: JobMetadata
}

// Generation Preview Types
export interface ReferencePreview {
  path: string
  role: string
  exists: boolean
  size_bytes?: number
}

export interface GeminiContentPart {
  type: 'text' | 'image'
  value?: string  // For text parts
  path?: string   // For image parts
  exists?: boolean // For image parts
}

export interface GeminiRequestPreview {
  model: string
  contents: GeminiContentPart[]
  config: {
    response_modalities: string[]
    image_config: {
      aspect_ratio: string
      image_size: string
    }
  }
}

export interface PanelGenerationPreview {
  panel_id: string
  panel_number: number
  scene_number: number
  chapter_number: number
  prompt: string
  gemini_request?: GeminiRequestPreview  // Optional for backwards compat with older API
  parameters: {
    aspect_ratio: string
    resolution: string
    style: string
    panel_type: string
    model: string
  }
  references: ReferencePreview[]
  panel_data: {
    action: string
    composition: {
      shot_type: string | null
      angle: string | null
    }
    characters: {
      character_id: string
      expression: string
      position: string
    }[]
    continues_from_previous: boolean
    continuity_note: string
  }
  location_name?: string
  time_of_day: string
  will_skip: boolean
}

export interface GenerationPreviewResponse {
  dry_run: true
  chapter_number: number
  scene_number?: number
  panel_number?: number
  total_panels: number
  panels_to_generate: number
  panels_to_skip: number
  panels: PanelGenerationPreview[]
}

// Trailer types
export interface TrailerSegment {
  index: number
  first_frame_path: string
  last_frame_path: string
  first_frame_description: string
  last_frame_description: string
  user_prompt: string
  refined_prompt: string
  video_path?: string
  duration: number
  metadata_path?: string
}

export interface TrailerConfig {
  frame_count: number
  segment_duration: number
  aspect_ratio: string
  generate_music: boolean
  visual_style: string
  music_volume: number
  fade_duration: number
}

export interface TrailerAssets {
  chapter_number: number
  config: TrailerConfig
  created_at: string
  selected_frames: string[]
  segments: TrailerSegment[]
  stitched_video?: string
  music_path?: string
  music_prompt?: string
  voiceover_script?: string
  voiceover_audio?: string
  voiceover_voice?: string
  final_video?: string
  total_duration?: number
  metadata_path?: string
}

export interface GenerateTrailerRequest {
  frame_count?: number
  segment_duration?: number
  aspect_ratio?: string
  generate_music?: boolean
  visual_style?: string
  music_volume?: number
  fade_duration?: number
  overwrite?: boolean
}

// I Feel Lucky Pipeline types
export type PipelineStageStatus = 'pending' | 'running' | 'completed' | 'failed' | 'skipped'

export interface PipelineStage {
  name: string
  status: PipelineStageStatus
  current: number
  total: number
  error?: string
}

export interface CharacterActorMapping {
  name: string
  actor_id: string
}

export interface IFeelLuckyRequest {
  prompt?: string
  style?: string
  episodes?: number
  characters?: CharacterActorMapping[]
  skip_trailer?: boolean
  skip_variants?: boolean
}

export interface IFeelLuckyResponse {
  job_id: string
  stages: PipelineStage[]
}

// Showcase types (for fetching published webtoons)
export interface ShowcaseChapter {
  number: number
  title: string
  summary: string
}

export interface ShowcaseWebtoon {
  id: string
  title: string
  description: string
  premise: string
  genre: string
  tags: string[]
  cover: string
  chapters: ShowcaseChapter[]
  has_celebrity?: boolean  // True if any character uses a celebrity actor
}

export interface ShowcasePagination {
  page: number
  limit: number
  total: number
  totalPages: number
  hasNext: boolean
  hasPrev: boolean
}

export interface ShowcaseWebtoonsResponse {
  webtoons: ShowcaseWebtoon[]
  pagination: ShowcasePagination
}

// Showcase chapter/panel types for Panel Sequencer
export interface ShowcaseDialogue {
  character_id: string
  text: string
  type: DialogueType
}

export interface ShowcaseSegment {
  id: string
  sequence: number
  type: 'panel'
  description: string
  characters: string[]
  dialogues: ShowcaseDialogue[]
  narration: string | null
  sfx: string
  shot_type: string
  mood: string
  scroll_pacing: string
  height_hint: string
  scene_id: string
  scene_title: string
  image_url: string | null
  has_image: boolean
}

export interface ShowcaseChapterDetail {
  webtoon_id: string
  webtoon_title: string
  chapter_number: number
  chapter_title: string
  chapter_summary: string
  total_chapters: number
  segments: ShowcaseSegment[]
  pagination: ShowcasePagination
}

// Dashboard Tabs (project grouping)
export type TabSortBy = 'name' | 'created_at' | 'updated_at' | 'manual'
export type TabSortDirection = 'asc' | 'desc'

export interface DashboardTab {
  id: string
  name: string
  position: number
  sort_by: TabSortBy
  sort_direction: TabSortDirection
  project_ids: string[]
}

// Actor library (Track D1)
export interface ActorStyleAssets {
  bible_sheet: string | null
  portrait: string | null
}

export interface ActorRecord {
  id: string
  name: string
  age: number | null
  reference_images: string[]
  bible_sheet: string | null
  portrait: string | null
  style_variants: Record<string, ActorStyleAssets>
  visual_tags: string[]
  is_celebrity: boolean
  created_at: string
  updated_at: string
}
